<script setup>
import { ref, computed, watch } from 'vue'
import { usePrestationStore } from '../../stores/prestationStore'

const prestationStore = usePrestationStore()

// Form state
const selectedDateId = ref('')
const selectedCreneauId = ref('')
const isSubmitting = ref(false)

// Computed
const isModalVisible = computed(() => prestationStore.dateModal.show)
const currentDate = computed(() => prestationStore.dateModal.currentDate)
const currentCreneau = computed(() => prestationStore.dateModal.currentCreneau)
const prestationId = computed(() => prestationStore.dateModal.prestationId)

// Computed pour les créneaux de la date sélectionnée
const availableCreneaux = computed(() => {
  if (!selectedDateId.value) return []
  return prestationStore.getCreneauxForDate(selectedDateId.value)
})

// Computed pour formater la date
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR')
}

// Watchers
// Réinitialiser le créneau quand la date change
watch(selectedDateId, () => {
  selectedCreneauId.value = ''
})

// Methods
const closeModal = () => {
  prestationStore.closeDateModal()
  selectedDateId.value = ''
  selectedCreneauId.value = ''
}

const handleSubmit = async () => {
  if (!selectedDateId.value || !selectedCreneauId.value) {
    return
  }

  isSubmitting.value = true
  try {
    await prestationStore.updateDate(prestationId.value, parseInt(selectedDateId.value), parseInt(selectedCreneauId.value))
    // TODO: Success notification
    console.log('Date modifiée avec succès')
    closeModal()
  } catch (error) {
    // TODO: Error notification
    console.error('Erreur lors de la modification:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <!-- Modal Modifier Date -->
  <div v-if="isModalVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Modifier la date</h3>
        <button type="button" class="modal-close" @click="closeModal">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="date-form">
          <p class="current-date">
            Date actuelle : <strong>{{ currentDate || 'Non définie' }}</strong>
            <span v-if="currentCreneau" class="current-creneau">
              {{ currentCreneau }}
            </span>
          </p>
          
          <!-- Formulaire de sélection -->
          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="date-select" class="form-label">
                Nouvelle date :
              </label>
              <select 
                id="date-select"
                v-model="selectedDateId"
                class="form-select"
                :disabled="prestationStore.loading.dates || isSubmitting"
                required
              >
                <option value="">-- Sélectionner une date --</option>
                <option 
                  v-for="(date, key) in prestationStore.availableDates" 
                  :key="key"
                  :value="key"
                >
                  {{ date.date_human }}
                </option>
              </select>
              
              <!-- Loading state -->
              <div v-if="prestationStore.loading.dates" class="loading-text">
                <i class="bi bi-arrow-repeat spin"></i>
                Chargement des dates disponibles...
              </div>
              
              <!-- Empty state -->
              <div v-else-if="Object.keys(prestationStore.availableDates).length === 0" class="empty-text">
                <i class="bi bi-info-circle"></i>
                Aucune date disponible pour le moment
              </div>
            </div>

            <!-- Créneau selection (requis) -->
            <div v-if="selectedDateId" class="form-group">
              <label for="creneau-select" class="form-label">
                Créneau :
              </label>
              <select 
                id="creneau-select"
                v-model="selectedCreneauId"
                class="form-select"
                :disabled="isSubmitting"
                required
              >
                <option value="">-- Sélectionner un créneau --</option>
                <option 
                  v-for="creneau in availableCreneaux" 
                  :key="creneau.circuit_date_creneau_id"
                  :value="creneau.circuit_date_creneau_id"
                >
                  {{ creneau.creneau_human }}
                </option>
              </select>
              
              <!-- Empty state pour créneaux -->
              <div v-if="selectedDateId && availableCreneaux.length === 0" class="empty-text">
                <i class="bi bi-info-circle"></i>
                Aucun créneau disponible pour cette date
              </div>
            </div>

            <!-- Error message -->
            <div v-if="prestationStore.error" class="error-message">
              <i class="bi bi-exclamation-triangle"></i>
              {{ prestationStore.error }}
            </div>
          </form>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          type="button" 
          class="btn btn-secondary" 
          @click="closeModal"
          :disabled="isSubmitting"
        >
          Annuler
        </button>
        <button 
          type="button" 
          class="btn btn-primary" 
          @click="handleSubmit"
          :disabled="!selectedDateId || !selectedCreneauId || isSubmitting || prestationStore.loading.dates"
        >
          <i v-if="isSubmitting" class="bi bi-arrow-repeat spin me-1"></i>
          {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  color: #6c757d;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
}

.current-date {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.current-creneau {
  display: block;
  font-weight: normal;
  color: #6c757d;
  margin-top: 0.25rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.loading-text,
.empty-text {
  margin-top: 0.5rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-text {
  background-color: #eff6ff;
  color: #1e40af;
}

.empty-text {
  background-color: #fef3c7;
  color: #92400e;
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5c636a;
}

.btn-primary {
  background-color: #0d6efd;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0b5ed7;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.me-1 {
  margin-right: 0.25rem;
}
</style> 